import { useMemo } from "react"
import { type Query as CubeQuery, type BinaryFilter } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDate from "~/ui-rtk/hooks/date"
import { getPivotData } from "~/ui-rtk/utils/cube"

/**
 * ============================================================================
 * CONSTANTS
 * ============================================================================
 */

// Ad metrics fanned out per matched product (EXPOSURE attribution — non-additive
// across products). Query A.
const ADS_BY_PRODUCT_CUBE = "company_media_metrics_ads_by_product"
// Product sales / web analytics. Query B.
const PAGE_PATH_REPORT_CUBE = "company_web_analytics_page_path_report"

/** Match type for the ad-by-product exposure attribution. */
export type MatchType = "main" | "all"

// Fetch everything in one page and merge/sort client-side: we cannot
// server-side sort across two independent cube queries.
const ITEMS_PER_PAGE = 10000

/**
 * ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================
 */

/**
 * One merged product row: ad EXPOSURE metrics (Query A) + sales metrics (Query
 * B), full-outer-joined on product handle.
 */
export interface ProductMediaPnLRow {
  productHandle: string
  productTitle: string
  // Ad exposure metrics (company_media_metrics_ads_by_product)
  activeAds: number
  totalSpent: number
  modeledRevenue: number
  platformRevenue: number
  brandValue: number
  modeledBrandValue: number
  // Sales metrics (company_web_analytics_page_path_report)
  totalRevenue: number
  newCustomerRevenue: number
  firstOrders: number
  sessions: number
}

/**
 * ============================================================================
 * MAIN HOOK
 * ============================================================================
 */
export default function useConnect(
  dateRange?: (string | undefined)[],
  matchType: MatchType = "main",
) {
  const currentCompany = useCurrentCompany()
  const { dateRange: fallbackDateRange } = useMinMaxDateRange()
  const dayjs = useDate()

  const companyId = currentCompany?.id || ""
  const effectiveDateRange =
    dateRange && dateRange[0] && dateRange[1]
      ? [dateRange[0], dateRange[1]]
      : [
          dayjs(fallbackDateRange[0]).format("YYYY-MM-DD"),
          dayjs(fallbackDateRange[1]).format("YYYY-MM-DD"),
        ]
  const startDate = effectiveDateRange[0] as string
  const endDate = effectiveDateRange[1] as string

  // ============================================================================
  // QUERY A — Ad metrics by product (exposure attribution)
  // ============================================================================
  const adsQuery = useMemo(() => {
    const filters: BinaryFilter[] = [
      {
        member: `${ADS_BY_PRODUCT_CUBE}.companyid`,
        operator: "equals",
        values: [companyId],
      },
      // ALWAYS pin match_type to exactly one value (never sum across both).
      {
        member: `${ADS_BY_PRODUCT_CUBE}.match_type`,
        operator: "equals",
        values: [matchType],
      },
    ]

    return {
      dimensions: [`${ADS_BY_PRODUCT_CUBE}.product_handle`],
      measures: [
        `${ADS_BY_PRODUCT_CUBE}.ad_count_with_spend`,
        `${ADS_BY_PRODUCT_CUBE}.spend`,
        `${ADS_BY_PRODUCT_CUBE}.modeled_revenue`,
        `${ADS_BY_PRODUCT_CUBE}.purchases`,
        `${ADS_BY_PRODUCT_CUBE}.brand_value_agg`,
        `${ADS_BY_PRODUCT_CUBE}.estimated_d180_brand_value`,
      ],
      filters,
      // Full-range totals (no time grain) so the granularity matches Query B.
      timeDimensions: [
        {
          dimension: `${ADS_BY_PRODUCT_CUBE}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
      limit: ITEMS_PER_PAGE,
    } as CubeQuery
  }, [companyId, matchType, startDate, endDate])

  // ============================================================================
  // QUERY B — Product sales / web analytics
  // ============================================================================
  const salesQuery = useMemo(
    () =>
      ({
        dimensions: [
          `${PAGE_PATH_REPORT_CUBE}.handle`,
          `${PAGE_PATH_REPORT_CUBE}.title`,
        ],
        measures: [
          `${PAGE_PATH_REPORT_CUBE}.total_revenue`,
          `${PAGE_PATH_REPORT_CUBE}.sum_first_order_revs`,
          `${PAGE_PATH_REPORT_CUBE}.first_orders`,
          `${PAGE_PATH_REPORT_CUBE}.sessions`,
        ],
        // Explicit company filter (defence-in-depth; queryRewrite also injects it),
        // matching the per-sub-query pattern in the sibling WebMerchandising connects.
        filters: [
          {
            member: `${PAGE_PATH_REPORT_CUBE}.companyid`,
            operator: "equals",
            values: [companyId],
          },
        ],
        timeDimensions: [
          {
            dimension: `${PAGE_PATH_REPORT_CUBE}.md_date`,
            dateRange: [startDate, endDate],
          },
        ],
        limit: ITEMS_PER_PAGE,
      }) as CubeQuery,
    [companyId, startDate, endDate],
  )

  const {
    resultSet: adsResultSet,
    isLoading: isAdsLoading,
    error: adsError,
  } = useCubeQuery(adsQuery)
  const {
    resultSet: salesResultSet,
    isLoading: isSalesLoading,
    error: salesError,
  } = useCubeQuery(salesQuery)

  // ============================================================================
  // MERGE — full-outer on product handle. Seed the map from BOTH result sets so
  // ad-only and sales-only products both appear.
  // ============================================================================
  const tableData = useMemo<ProductMediaPnLRow[]>(() => {
    // Nothing to merge until at least one query has resolved; the loading overlay
    // covers the transient single-side state (isLoading stays true until both finish).
    if (!adsResultSet && !salesResultSet) return []

    const rowsByHandle = new Map<string, ProductMediaPnLRow>()

    const ensureRow = (handle: string): ProductMediaPnLRow => {
      let row = rowsByHandle.get(handle)
      if (!row) {
        row = {
          productHandle: handle,
          productTitle: "",
          activeAds: 0,
          totalSpent: 0,
          modeledRevenue: 0,
          platformRevenue: 0,
          brandValue: 0,
          modeledBrandValue: 0,
          totalRevenue: 0,
          newCustomerRevenue: 0,
          firstOrders: 0,
          sessions: 0,
        }
        rowsByHandle.set(handle, row)
      }
      return row
    }

    // Query A — ad exposure metrics
    if (adsResultSet) {
      const adsRows = getPivotData(adsResultSet, adsQuery)
      adsRows.forEach((raw: Record<string, unknown>) => {
        const handle = String(
          raw[`${ADS_BY_PRODUCT_CUBE}.product_handle`] || "",
        )
        if (!handle) return
        const row = ensureRow(handle)
        row.activeAds = Number(
          raw[`${ADS_BY_PRODUCT_CUBE}.ad_count_with_spend`] || 0,
        )
        row.totalSpent = Number(raw[`${ADS_BY_PRODUCT_CUBE}.spend`] || 0)
        row.modeledRevenue = Number(
          raw[`${ADS_BY_PRODUCT_CUBE}.modeled_revenue`] || 0,
        )
        row.platformRevenue = Number(
          raw[`${ADS_BY_PRODUCT_CUBE}.purchases`] || 0,
        )
        row.brandValue = Number(
          raw[`${ADS_BY_PRODUCT_CUBE}.brand_value_agg`] || 0,
        )
        row.modeledBrandValue = Number(
          raw[`${ADS_BY_PRODUCT_CUBE}.estimated_d180_brand_value`] || 0,
        )
      })
    }

    // Query B — sales metrics
    if (salesResultSet) {
      const salesRows = getPivotData(salesResultSet, salesQuery)
      salesRows.forEach((raw: Record<string, unknown>) => {
        const handle = String(raw[`${PAGE_PATH_REPORT_CUBE}.handle`] || "")
        if (!handle) return
        const row = ensureRow(handle)
        const title = raw[`${PAGE_PATH_REPORT_CUBE}.title`]
        if (title) {
          row.productTitle = String(title)
        }
        row.totalRevenue = Number(
          raw[`${PAGE_PATH_REPORT_CUBE}.total_revenue`] || 0,
        )
        row.newCustomerRevenue = Number(
          raw[`${PAGE_PATH_REPORT_CUBE}.sum_first_order_revs`] || 0,
        )
        row.firstOrders = Number(
          raw[`${PAGE_PATH_REPORT_CUBE}.first_orders`] || 0,
        )
        row.sessions = Number(raw[`${PAGE_PATH_REPORT_CUBE}.sessions`] || 0)
      })
    }

    return Array.from(rowsByHandle.values())
  }, [adsResultSet, salesResultSet, adsQuery, salesQuery])

  return {
    isLoading: isAdsLoading || isSalesLoading,
    error: adsError || salesError,
    tableData,
  }
}
